import React, { useEffect, useRef, useState } from 'react';
import { KitIcons } from '@repo/apg-ui';

const LANGUAGES = ['de', 'en', 'fr', 'it'];

interface Props {
  language: string;
  onLanguageChange: (lang: string) => void;
}

const LanguageSwitcher = ({ language, onLanguageChange }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (lang: string) => {
    setIsOpen(false);
    onLanguageChange(lang);
  };

  const ChevIcon = isOpen ? KitIcons.ChevUpIcon : KitIcons.ChevDownIcon;

  return (
    <div className='language-switcher' ref={containerRef}>
      <button
        type='button'
        className='language-switcher__trigger'
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-label='Sprache auswählen'
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {language.toUpperCase()}
        <ChevIcon className='language-switcher__chevron' />
      </button>
      {isOpen && (
        <ul className='language-switcher__dropdown' role='listbox'>
          {LANGUAGES.filter((lang) => lang !== language).map((lang) => (
            <li key={lang}>
              <button
                type='button'
                role='option'
                aria-selected={false}
                className='language-switcher__option'
                onClick={() => handleSelect(lang)}
              >
                {lang.toUpperCase()}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
